import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  AttentionItem,
  ActivityGroup,
  ActivityItem,
  Project,
  Workspace,
  WorkspacePlan,
} from '@katsquare/types';
import { Spinner } from '../components/ui';
import { IconSearch } from '../components/icons';
import { getAccountOverview, listProjects, listWorkspaces, searchAll } from '../lib/api';
import { timeAgo } from '../lib/format';
import { PLAN_LABEL } from '../lib/plans';
import { useSession } from '../features/auth/useSession';

/** Default dashboard: an at-a-glance overview of the account's workspaces. */
export function ProjectsIndex() {
  const { user } = useSession();
  const workspaces = useQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces });

  const firstName = user?.name?.split(' ')[0] ?? 'there';

  return (
    <div className="h-full overflow-y-auto bg-neutral-50">
      <div className="mx-auto max-w-4xl px-6 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
            Welcome back, {firstName}
          </h1>
          <p className="mt-1 text-sm text-neutral-500">Here's an overview of your workspaces.</p>
        </header>

        <GlobalSearch />

        <section className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-400">
            Workspaces
          </h2>
          {workspaces.isPending ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : workspaces.data && workspaces.data.length > 0 ? (
            <div className="space-y-6">
              {workspaces.data.map((ws) => (
                <WorkspaceCard key={ws.id} workspace={ws} />
              ))}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-neutral-300 bg-white px-4 py-8 text-center text-sm text-neutral-400">
              Create a workspace and magazine in the sidebar to get started.
            </p>
          )}
        </section>

        <Overview />

        <PlanCard plan={user?.plan ?? 'FREE'} periodEnd={user?.currentPeriodEnd ?? null} />
      </div>
    </div>
  );
}

/**
 * The two things worth knowing on arrival: what is waiting on you, and what the
 * rest of the team has been doing. A brand-new account has neither, so the whole
 * block stays out of the way until there is something to say.
 */
function Overview() {
  const overview = useQuery({ queryKey: ['overview'], queryFn: getAccountOverview });
  const data = overview.data;
  if (!data || (data.attention.length === 0 && data.activity.length === 0)) return null;

  const hidden = data.attentionTotal - data.attention.length;
  return (
    // Five columns so the split lands on 40/60: attention is a short list of
    // names, activity is prose that reads better with the extra room.
    <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-5">
      <section className="rounded-2xl border border-neutral-200 bg-white p-5 lg:col-span-2">
        <div className="mb-3 flex items-baseline gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-400">
            Needs your attention
          </h2>
          {data.attentionTotal > 0 && (
            <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-[11px] font-semibold text-white">
              {data.attentionTotal}
            </span>
          )}
        </div>
        {data.attention.length === 0 ? (
          <p className="py-4 text-sm text-neutral-400">
            Nothing waiting on you — every PDF is approved or still rendering.
          </p>
        ) : (
          <ul className="-mx-2 space-y-0.5">
            {data.attention.map((it) => (
              <AttentionRow key={it.proofId} item={it} />
            ))}
          </ul>
        )}
        {hidden > 0 && (
          <p className="mt-2 px-2 text-xs text-neutral-400">+{hidden} more waiting</p>
        )}
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5 lg:col-span-3">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-400">
          Recent activity
        </h2>
        {data.activity.length === 0 ? (
          <p className="py-4 text-sm text-neutral-400">Nothing has happened yet.</p>
        ) : (
          <div className="space-y-4">
            {data.activity.map((group, i) => (
              // A recycled magazine and a purged one both come back without an
              // id, so key on the first event instead — that one is always unique.
              <ActivityGroupBlock
                key={group.projectId ?? group.items[0]?.id}
                group={group}
                // Two workspaces can each run a magazine called "Regular"; say
                // which is which only when both are on screen.
                showWorkspace={data.activity.some(
                  (g, j) => j !== i && g.projectName === group.projectName,
                )}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** Why this PDF is in the list — the phrasing the row leads with. */
const ATTENTION_REASON: Record<AttentionItem['kind'], { label: string; dot: string }> = {
  mention: { label: 'mentions you', dot: 'bg-amber-500' },
  changes: { label: 'Changes requested', dot: 'bg-red-500' },
  approval: { label: 'Awaiting approval', dot: 'bg-blue-500' },
};

function AttentionRow({ item }: { item: AttentionItem }) {
  const reason = ATTENTION_REASON[item.kind];
  const label =
    item.kind === 'mention'
      ? `${item.mentionCount} thread${item.mentionCount === 1 ? '' : 's'} ${reason.label}`
      : reason.label;
  return (
    <li>
      <Link
        to={`/pdfs/${item.proofId}`}
        className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-neutral-50"
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${reason.dot}`} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-neutral-800">
            {item.proofName}
          </span>
          <span className="block truncate text-xs text-neutral-400">
            {item.context} · v{item.versionNumber}
          </span>
        </span>
        <span className="shrink-0 text-xs text-neutral-500">{label}</span>
      </Link>
    </li>
  );
}

/**
 * One magazine's events under its own heading. The magazine is named once at the
 * top instead of on every row — the rows are already prose, and repeating the
 * title on each of them would drown the sentence it belongs to.
 */
function ActivityGroupBlock({
  group,
  showWorkspace,
}: {
  group: ActivityGroup;
  showWorkspace: boolean;
}) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold text-neutral-700">
        {group.projectId ? (
          <Link to={`/projects/${group.projectId}`} className="hover:underline">
            {group.projectName}
          </Link>
        ) : (
          <span className="text-neutral-400">{group.projectName}</span>
        )}
        {showWorkspace && group.workspaceName && (
          <span className="font-normal text-neutral-400"> · {group.workspaceName}</span>
        )}
      </p>
      <ul className="space-y-2.5 border-l border-neutral-100 pl-3">
        {group.items.map((e) => (
          <ActivityRow key={e.id} event={e} />
        ))}
      </ul>
    </div>
  );
}

function ActivityRow({ event }: { event: ActivityItem }) {
  // The summary already reads as a sentence ("Approved \"Approval\""), so the
  // actor's first name in front of it is all the framing it needs — except for
  // the one summary written about the review rather than about its author.
  // Guest events have no actor at all and name themselves instead.
  const who = event.actorName?.split(' ')[0] ?? null;
  const said = event.type === 'review.approved' ? 'fully approved this version' : event.summary;
  return (
    <li className="flex items-baseline gap-2 text-sm">
      <span className="min-w-0 flex-1 text-neutral-600">
        {who && <span className="font-medium text-neutral-800">{who} </span>}
        {said}
        {event.proofName &&
          (event.proofId ? (
            <>
              {' — '}
              <Link to={`/pdfs/${event.proofId}`} className="text-neutral-800 hover:underline">
                {event.proofName}
              </Link>
            </>
          ) : (
            ` — ${event.proofName}`
          ))}
      </span>
      <span className="shrink-0 text-xs text-neutral-400" title={new Date(event.createdAt).toLocaleString()}>
        {timeAgo(event.createdAt)}
      </span>
    </li>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const results = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => searchAll(debounced),
    enabled: debounced.length >= 2,
  });
  const data = results.data;
  const hasResults = !!data && (data.magazines.length > 0 || data.months.length > 0 || data.pdfs.length > 0);

  return (
    <div className="relative mb-6">
      <div className="relative">
        <IconSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search magazines, months and PDFs…"
          className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-10 pr-10 text-sm shadow-sm outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ('')}
            title="Clear search"
            className="absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            ✕
          </button>
        )}
      </div>

      {debounced.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-lg">
          {results.isPending ? (
            <div className="flex justify-center py-5">
              <Spinner />
            </div>
          ) : hasResults ? (
            <div className="max-h-96 overflow-y-auto py-1">
              <SearchGroup
                label="Magazines"
                items={data!.magazines.map((m) => ({ to: `/projects/${m.id}`, name: m.name, sub: m.workspaceName }))}
                onNavigate={() => setQ('')}
              />
              <SearchGroup
                label="Months"
                items={data!.months.map((m) => ({
                  to: `/projects/${m.projectId}?issue=${m.id}`,
                  name: m.name,
                  sub: m.context,
                }))}
                onNavigate={() => setQ('')}
              />
              <SearchGroup
                label="PDFs"
                items={data!.pdfs.map((p) => ({ to: `/pdfs/${p.id}`, name: p.name, sub: p.context }))}
                onNavigate={() => setQ('')}
              />
            </div>
          ) : (
            <p className="px-4 py-4 text-center text-sm text-neutral-400">No matches for “{debounced}”.</p>
          )}
        </div>
      )}
    </div>
  );
}

function SearchGroup({
  label,
  items,
  onNavigate,
}: {
  label: string;
  items: { to: string; name: string; sub: string }[];
  onNavigate: () => void;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
        {label}
      </p>
      {items.map((it) => (
        <Link
          key={it.to}
          to={it.to}
          onClick={onNavigate}
          className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-neutral-50"
        >
          <span className="min-w-0 truncate text-sm font-medium text-neutral-800">{it.name}</span>
          <span className="shrink-0 truncate text-xs text-neutral-400">{it.sub}</span>
        </Link>
      ))}
    </div>
  );
}

function PlanCard({ plan, periodEnd }: { plan: WorkspacePlan; periodEnd: string | null }) {
  const renews = periodEnd ? new Date(periodEnd) : null;
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-neutral-200 bg-white px-5 py-4">
      <div className="flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-400">Current plan</p>
        <p className="mt-0.5 text-lg font-semibold text-neutral-900">{PLAN_LABEL[plan]}</p>
        {renews && (
          <p className="mt-0.5 text-xs text-neutral-500">
            Renews{' '}
            {renews.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        )}
      </div>
      {plan !== 'BUSINESS' && (
        <Link
          to="/pricing"
          className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-semibold text-white hover:bg-neutral-700"
        >
          Upgrade plan
        </Link>
      )}
    </div>
  );
}

function WorkspaceCard({ workspace }: { workspace: Workspace }) {
  const projects = useQuery({
    queryKey: ['projects', workspace.id],
    queryFn: () => listProjects(workspace.id),
  });

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5">
      {/* Plan is per-account (shown in the plan card above), so no per-workspace badge. */}
      <div className="mb-3 flex items-center gap-2">
        <h3 className="font-semibold text-neutral-900">{workspace.name}</h3>
      </div>
      {projects.isPending ? (
        <div className="py-4">
          <Spinner />
        </div>
      ) : projects.data && projects.data.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {projects.data.map((p: Project) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}`}
              className="group flex items-center gap-2.5 rounded-lg border border-neutral-200 px-3 py-2.5 transition-colors hover:border-neutral-300 hover:bg-neutral-50 hover:shadow-sm"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-neutral-500 transition-colors group-hover:bg-neutral-900 group-hover:text-white">
                <BookIcon />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-neutral-800">
                {p.name}
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-400">No magazines yet.</p>
      )}
    </div>
  );
}

/** Small book glyph for magazine cards (matches the sidebar icon). */
function BookIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M4 5a2 2 0 0 1 2-2h9v18H6a2 2 0 0 1-2-2z" />
      <path d="M15 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3" />
    </svg>
  );
}
